import React, { useEffect, useState, useCallback, useMemo, useRef, useTransition } from 'react';
import { CircularProgress } from '@mui/material';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QueryString from 'qs';
import { useToast } from 'use-toast-mui';
import { useTranslation } from 'react-i18next';

// API
import { createLesson, updateLesson, deleteWeekById, getLessonById } from '../../../api/lesson';
import { getUserTickets } from '../../../api/user';
import { getTicketsByClassTime } from '../../../api/ticket';
import { getConsultationById, updateConsultation } from '../../../api/consultation';
import { withOverlapConfirm } from '../../../utils/overlapConfirm';
import { getBranchScheduleById, updateBranchSchedule } from '../../../api/branchSchedule';
import { updateBranchTrainerOrder } from '../../../api/staff';
import { getExpirationAlerts } from '../../../api/expiration';

// 컴포넌트
import PrivateLessonAttendanceModal from './PrivateLessonAttendanceModal';
import LessonUsers from '../LessonUsers';
import { EventMenu as GroupEventMenu } from '../groupLesson/GroupLessonModals';
import PrivateLessonEditModal from '../lessonModal/PrivateLessonEditModal/PrivateLessonEditModal';
import PrivateLessonHeader from './PrivateLessonHeader';
import PrivateLessonCalendar from './PrivateLessonCalendar';
import PrivateLessonMonthView from './PrivateLessonMonthView';
import PrivateLessonWeekView from './PrivateLessonWeekView';
import { TrainerFilterModal, CancelConfirmModal, EventDetailMenu } from './PrivateLessonModals';
import { FetchingBadge } from './PrivateLessonStyled';
import ConsultationDetailModal from './ConsultationDetailModal';
import ScheduleDropModal from './ScheduleDropModal';
import { useScheduleMoveConfirm } from './ScheduleMoveConfirm';
import BranchScheduleSettingModal from './BranchScheduleSettingModal';
import {
  useBranchScheduleSetting,
  getCreateDefaults,
  getHiddenClassTypes,
  isHiddenConflict,
  FILTER_VALUE_TO_COUNT_KEY,
  withHiddenTypeNote,
} from './branchScheduleSetting';
import { dayOfWeekName, isSameStart } from './scheduleMove';
import SchedulePasteLayer from '../scheduleClipboard/SchedulePasteLayer';
import { MoveTrainerAssignModal } from '../scheduleClipboard/SchedulePasteDialogs';
import { checkMoveTrainerAssignment } from '../scheduleClipboard/pasteSchedule';
import { useSchedulePaste } from '../scheduleClipboard/useSchedulePaste';
import { useScheduleDelete } from '../scheduleClipboard/useScheduleDelete';

// 훅
import { usePrivateLessonData, useMemberSearch, getDayOfWeek, getWeekDates } from './hooks/usePrivateLessonData';
import { endOfWeekBy, startOfWeekBy } from '../weekStart';
import { PANEL_MIN_WIDTH, PANEL_MAX_WIDTH } from './hooks/usePanelResize';

// 유틸
import { errorMessages } from '../../../utils/methods';
import { scrollToCard } from '../../../utils/scrollToCard';
import useUserAuth from '../../../utils/useUserAuth';
import {
  summarizeEvents,
  matchesTypeFilter,
  getLessonAttendStatus,
  TYPE_FILTER_ALL,
  TYPE_FILTER_CONSULT,
  TYPE_FILTER_GROUP,
  TYPE_FILTER_OT,
  TYPE_FILTER_PRIVATE,
  getGroupReserveStatus,
  isOtGroupEvent,
  otTicketValue,
} from './lessonStatus';

const normalizeMemberTickets = (response, targetLessonType) => {
  const regular = [...(response?.activeTickets || []), ...(response?.activePasses || [])].map(ticket => ({
    ...ticket,
    ticketType: 'COURSE_TICKET',
    _id: ticket.courseTicketId ?? ticket.memberCourseTicketId, // 상품ID (payload tickets용)
    // 회원 티켓 단위 유일 키 — 같은 상품 2개 보유 시 select 식별/오차감 방지
    optionKey: ticket.memberCourseTicketId ?? ticket.courseTicketId,
    _name: ticket.courseTicketName || ticket.ticketName,
  }));

  const trial = (response?.activeTrialTickets || []).map(ticket => ({
    ...ticket,
    ticketType: 'TRIAL_TICKET',
    _id: ticket.trialTicketId, // 상품ID (payload trialTickets용)
    optionKey: ticket.memberTrialTicketId ?? ticket.trialTicketId,
    _name: ticket.trialName,
  }));

  // 기간이 안 맞는 상품도 목록에는 둔다 — 고르는 순간 수업 날짜와 기간을 비교해 '이용기간 밖' 팝업을 띄운다(등록 모달)
  return [...regular, ...trial].filter(
    ticket => ticket.classType === targetLessonType && ticket.remainingCount > 0,
  );
};

// 모달 레슨구분(lessonType) → 저장 courseClassType. 듀엣·OT 는 그대로, 나머지(개인)는 PRIVATE_LESSON.
const lessonTypeToClassType = lessonType =>
  lessonType === 'DUET_LESSON' || lessonType === 'OT' ? lessonType : 'PRIVATE_LESSON';

// 자정부터 분 → 'HH:mm'
const toHHmm = totalMinutes =>
  `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;

// 상담 시간 파싱 — 문자열("09:00:00") 또는 객체({hour, minute}) 모두 대응
const parseTimeParts = timeValue => {
  if (!timeValue) return { hour: 0, minute: 0 };
  if (typeof timeValue === 'string') {
    const parts = timeValue.split(':');
    return { hour: parseInt(parts[0], 10) || 0, minute: parseInt(parts[1], 10) || 0 };
  }
  return { hour: timeValue.hour ?? 0, minute: timeValue.minute ?? 0 };
};

// 뷰 상태 localStorage 키 ('month' | 'week' | 'day')
const VIEW_STORAGE_KEY = 'privateLessonView';
const VIEW_VALUES = ['month', 'week', 'day'];

// 기간 조회 최대 일수 — 데이터량과 월간 그리드 표현 한계 (사용자 확정)
const RANGE_MAX_DAYS = 31;

// 뷰별 줌 설정 — day=시간셀 높이, week=1시간 픽셀, month=날짜셀 높이
const ZOOM_CONFIG = {
  day: { base: 80, min: 40, max: 120, step: 10, storageKey: 'privateLessonCellHeight' },
  week: { base: 52, min: 36, max: 104, step: 8, storageKey: 'privateLessonWeekHourHeight' },
  month: { base: 128, min: 96, max: 256, step: 16, storageKey: 'privateLessonMonthCellHeight' },
};

const loadZoomValue = viewKey => {
  const { storageKey, base, min, max } = ZOOM_CONFIG[viewKey];
  const saved = Number(localStorage.getItem(storageKey));
  return saved ? Math.min(Math.max(saved, min), max) : base;
};

// 필터 상태를 모듈 스코프에 들고 있어 다른 메뉴를 다녀와도 유지한다.
// 스토리지를 쓰지 않으므로 새로고침·창 닫기에는 자연히 초기화되고, 지점이 바뀌면 버린다.
let filterMemo = {
  branchId: null,
  selectedTrainerIds: [],
  attendFilters: [],
  consultResultFilters: [],
  groupStatusFilters: [],
  typeFilter: TYPE_FILTER_ALL,
};

// 충돌 포커스에서 카드를 못 찾았을 때 재시도 간격
const FOCUS_RETRY_MS = 300;
// 유형=OT 상태 칩 → 이벤트 인원 수 필드
const OT_STATUS_COUNT_KEY = {
  reserved: 'reservedMemberCount',
  attended: 'attendedMemberCount',
  absented: 'absentedMemberCount',
};
// 전체 보기에서만 쓰는 유형 카테고리 칩 (예약/출석/결석 상태 칩과 구분)
const CATEGORY_CHIP_VALUES = ['group', 'ot', 'consult', 'etc'];

const readFilterMemo = branchId => (filterMemo.branchId === branchId ? filterMemo : null);

/**
 * 개인 레슨 목록 컴포넌트 (메인 컨테이너)
 */
const PrivateLessonList = ({ courseClassType, periodTimes: savedPeriodTimes, onPeriodTimesSaved }) => {
  const { user } = useSelector(state => state.user);
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  // 권한 체크
  const canPrivateEdit = useUserAuth('SCHEDULE_PRIVATE_EDIT');
  const canPrivateAttendance = useUserAuth('SCHEDULE_PRIVATE_ATTENDANCE');
  // 우클릭 '일정 삭제' — 그룹수업 카드는 그룹 화면과 같은 그룹 수정 권한
  const canGroupEdit = useUserAuth('SCHEDULE_GROUP_EDIT');
  // 설정 모달의 '강의실 관리'는 센터설정 화면 권한이 있을 때만
  const canManageRooms = useUserAuth('CENTER_STORE_VIEW');

  // 지점 스케줄 설정 (시간 단위·카드 최소 높이·요약 카운트·기본 수업 시간/강의실). 조회 실패 시 기본값.
  const {
    setting: savedScheduleSetting,
    isLoading: scheduleSettingLoading,
    isError: scheduleSettingError,
  } = useBranchScheduleSetting(user?.branch?.branchId, user?.token);
  const [openScheduleSetting, setOpenScheduleSetting] = useState(false);
  // 설정 모달에서 바꾼 값의 미리보기 { setting, times } — 저장 전에도 화면은 이 값으로 그린다. 닫으면(저장·취소) 걷는다
  const [schedulePreview, setSchedulePreview] = useState(null);
  const scheduleSetting = schedulePreview?.setting ?? savedScheduleSetting;
  const periodTimes = schedulePreview?.times ?? savedPeriodTimes;
  const closeScheduleSetting = () => {
    setOpenScheduleSetting(false);
    setSchedulePreview(null);
  };
  // 선택 단위가 표 칸보다 크게 저장돼 있으면(표 30분 + 선택 1시간) 표 칸 단위로 동작한다
  const snapMinutes = Math.min(scheduleSetting.snapMinutes, scheduleSetting.timeUnitMinutes);
  // 표시를 끈 유형(그룹·OT·상담·기타) — 이 화면 어디에도 그리지 않는다
  const hiddenClassTypes = useMemo(
    () => getHiddenClassTypes(scheduleSetting.visibleCounts),
    [scheduleSetting.visibleCounts],
  );

  // ============================================
  // 상태 관리
  // ============================================
  // 상담 이력 등 다른 화면에서 특정 상담으로 넘어온 경우 (?date=YYYY-MM-DD&consultationId=N)
  const [searchParams, setSearchParams] = useSearchParams();
  const focusDate = searchParams.get('date');
  // 쿼리는 마운트 직후 지우므로, 포커스 대상은 첫 렌더 값으로 붙잡아 둔다 (이벤트 로드가 끝난 뒤 플래시)
  // 상담(consultationId)과 수업(focusClassTimeId·classId) 경로가 같은 플래시를 쓴다.
  const [pendingFocus, setPendingFocus] = useState(() => {
    const consultationId = searchParams.get('consultationId');
    if (consultationId) return { attr: 'data-consultation-id', value: consultationId };
    const classTimeId = searchParams.get('focusClassTimeId');
    if (classTimeId) return { attr: 'data-class-time-id', value: classTimeId };
    // 수업·상담 관리 수업 목록에서 넘어온 수업(courseClassId — 같은 날 첫 회차 카드로 간다)
    const classId = searchParams.get('classId');
    if (classId) return { attr: 'data-course-class-id', value: classId };
    return null;
  });

  const [currentDate, setCurrentDate] = useState(() => (focusDate ? moment(focusDate) : moment()));
  const [view, setView] = useState(() => {
    if (focusDate) return 'day';
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    return VIEW_VALUES.includes(saved) ? saved : 'day';
  });
  const [selectedDate, setSelectedDate] = useState(moment()); // 월/주 분할형 패널의 선택일
  // 기간 조회 (좌상단 달력 버튼) — {start, end} 'YYYY-MM-DD'. 있으면 뷰와 무관하게 월간 그리드로 표시한다.
  const [customRange, setCustomRange] = useState(null);
  const isRangeMode = !!customRange;
  const [selectedTrainerIds, setSelectedTrainerIds] = useState(
    () => readFilterMemo(user?.branch?.branchId)?.selectedTrainerIds ?? [],
  );
  const [openTrainerFilter, setOpenTrainerFilter] = useState(false);
  // 출석 상태 필터 (빈 배열 = 전체)
  const [attendFilters, setAttendFilters] = useState(() => readFilterMemo(user?.branch?.branchId)?.attendFilters ?? []);
  // 상담 결과 필터 (다중선택, 빈 배열 = 전체)
  const [consultResultFilters, setConsultResultFilters] = useState(
    () => readFilterMemo(user?.branch?.branchId)?.consultResultFilters ?? [],
  );
  // 수업 유형 필터 (전체/그룹/개인/상담 중 하나)
  const [typeFilter, setTypeFilter] = useState(
    () => readFilterMemo(user?.branch?.branchId)?.typeFilter ?? TYPE_FILTER_ALL,
  );
  // 그룹 예약상태 필터 (다중선택, 빈 배열 = 전체). 유형=그룹 칩에서만 조작·적용된다.
  const [groupStatusFilters, setGroupStatusFilters] = useState(
    () => readFilterMemo(user?.branch?.branchId)?.groupStatusFilters ?? [],
  );

  const toggleInList = (list, value) =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value];

  // 카운트 '스케줄' 칩 — 드롭다운 유형은 유지한 채 칩 필터만 전부 해제한다.
  const handleResetFilters = () => {
    setAttendFilters([]);
    setConsultResultFilters([]);
    setGroupStatusFilters([]);
  };

  const handleToggleConsultResultFilter = value =>
    setConsultResultFilters(prev => toggleInList(prev, value));
  const handleToggleGroupStatusFilter = value =>
    setGroupStatusFilters(prev => toggleInList(prev, value));

  useEffect(() => {
    if (!pendingFocus && !focusDate) return;
    setSearchParams({}, { replace: true });
    // 찾아갈 카드가 필터에 가려지지 않게 필터를 푼다
    if (pendingFocus) {
      setTypeFilter(TYPE_FILTER_ALL);
      setSelectedTrainerIds([]);
      handleResetFilters();
    }
    // 첫 렌더에서 읽은 값으로 날짜·포커스를 이미 잡았으므로 쿼리는 즉시 비운다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 필터 상태를 모듈 스코프에 보관 — 다른 메뉴를 다녀와도 복원된다.
  useEffect(() => {
    filterMemo = {
      branchId: user?.branch?.branchId,
      selectedTrainerIds,
      attendFilters,
      consultResultFilters,
      groupStatusFilters,
      typeFilter,
    };
  }, [user?.branch?.branchId, selectedTrainerIds, attendFilters, consultResultFilters, groupStatusFilters, typeFilter]);

  const handleViewChange = nextView => {
    localStorage.setItem(VIEW_STORAGE_KEY, nextView);
    setCustomRange(null); // 뷰 전환 시 기간 조회 자동 해제 (사용자 확정)
    setView(nextView);
    clampSelectedDate(currentDate, nextView);
  };

  // 줌 컨트롤 (뷰별 독립 상태)
  const [cellHeight, setCellHeight] = useState(() => loadZoomValue('day'));
  const [weekHourHeight, setWeekHourHeight] = useState(() => loadZoomValue('week'));
  const [monthCellHeight, setMonthCellHeight] = useState(() => loadZoomValue('month'));

  const zoomValues = { day: cellHeight, week: weekHourHeight, month: monthCellHeight };
  const zoomSetters = { day: setCellHeight, week: setWeekHourHeight, month: setMonthCellHeight };

  // 기간 조회 모드는 월간 그리드로 그리므로 줌도 month 설정을 쓴다 (주간 뷰는 주간 UI 확장이라 그대로)
  const zoomViewKey = isRangeMode && view !== 'week' ? 'month' : view;

  const handleZoom = direction => {
    const config = ZOOM_CONFIG[zoomViewKey];
    zoomSetters[zoomViewKey](prev => {
      const next =
        direction === 'in' ? Math.min(prev + config.step, config.max) : Math.max(prev - config.step, config.min);
      localStorage.setItem(config.storageKey, next);
      return next;
    });
  };

  // 우측 상세 패널 접힘/가로폭 (월/주 공유, localStorage 유지)
  const [panelCollapsed, setPanelCollapsed] = useState(
    () => localStorage.getItem('privateLessonPanelCollapsed') === '1',
  );
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem('privateLessonPanelWidth'));
    return saved ? Math.min(Math.max(saved, PANEL_MIN_WIDTH), PANEL_MAX_WIDTH) : null; // null = 뷰 기본폭
  });

  const togglePanelCollapsed = () => {
    setPanelCollapsed(prev => {
      localStorage.setItem('privateLessonPanelCollapsed', prev ? '0' : '1');
      return !prev;
    });
  };

  const handlePanelWidthChange = width => {
    setPanelWidth(width);
    localStorage.setItem('privateLessonPanelWidth', width);
  };

  // 월/주 날짜 선택 — 패널이 접혀 있으면 자동으로 펼쳐 상세보기를 보여준다
  const handleSelectDate = date => {
    setSelectedDate(date);
    setPanelCollapsed(prev => {
      if (prev) localStorage.setItem('privateLessonPanelCollapsed', '0');
      return false;
    });
  };

  // 모달/메뉴 상태
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [opens, setOpens] = useState({ open: false, open1: false, open2: false, open3: false });
  const [isUpdate, setIsUpdate] = useState(false);
  const [openConsultationDetail, setOpenConsultationDetail] = useState(false);
  // 강사 선택 모드 — 주간 패널 "+ 일정 추가" / 빈칸 클릭으로 생성 진입 시 담당강사를 직접 선택하게 함
  const [trainerSelectMode, setTrainerSelectMode] = useState(false);

  // 모달 데이터
  const [modalData, setModalData] = useState({
    column: null,
    index: null,
    branchId: user?.branch?.branchId,
    className: '',
    registrationStartDate: currentDate.toISOString().split('T')[0],
    registrationEndDate: currentDate.toISOString().split('T')[0],
    startHour: 0,
    startMinute: 0,
    endHour: 0,
    endMinute: 0,
    exerciseRoomId: -1,
    classTimeInfoList: [],
    trainerName: '',
    courseClassTimeId: null,
    memberId: null,
    memberName: null,
    courseClassId: null,
    memo: '',
    tickets: [],
    counselManagerId: '',
    counselManagerName: '',
    consultMethod: '',
    consultPurpose: '',
    consultationId: null,
    courseTicketDeductionCount: 1,
    lessonType: 'PRIVATE_LESSON',
  });

  // 검색/수강권 상태
  const [searchResults, setSearchResults] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [originalTickets, setOriginalTickets] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  // OT 생성용: 선택 회원별 OT 수강권/일일권 목록 { [memberId]: normalizedTickets[] }. 회원마다 자기 수강권으로 차감된다.
  const [otMemberTickets, setOtMemberTickets] = useState({});
  // 회원별 OT 수강권 조회 실패 여부 — '수강권 없음'과 구분해 다시 시도할 수 있게 한다
  const [otTicketErrors, setOtTicketErrors] = useState({});
  const [isMemberChanged, setIsMemberChanged] = useState(false);
  const [trainerId, setTrainerId] = useState(null);

  // 시간 관련
  const [hours, setHours] = useState([]);

  // React Query 훅
  const { staffList: savedStaffList, rooms, events, isLoading, isFetchingEvents, refetchEvents } = usePrivateLessonData({
    branchId: user?.branch?.branchId,
    headBranchId: user?.headBranch?.headBranchId,
    token: user?.token,
    currentDate,
    view,
    customRange,
    weekStartDay: scheduleSetting.weekStartDay,
  });

  // 직원(컬럼) 순서 — 설정 모달에서 바꾼 순서를 저장 전에도 미리보기로 쓴다
  const staffList = useMemo(() => {
    const order = schedulePreview?.staffOrder;
    if (!order) return savedStaffList;
    const byId = new Map(savedStaffList.map(staff => [staff.trainerId, staff]));
    return order.map(id => byId.get(id)).filter(Boolean);
  }, [savedStaffList, schedulePreview?.staffOrder]);

  // 넘어온 카드(상담 또는 수업)로 스크롤하고 잠깐 링을 띄운다. 쿼리는 한 번 쓰고 지워서 새로고침에 다시 튀지 않게 한다.
  useEffect(() => {
    if (!pendingFocus) return undefined;

    // expiresAt 은 충돌 포커스에만 있다. URL 파라미터 포커스는 없으므로 기존처럼 events 갱신을 기다린다.
    const { expiresAt } = pendingFocus;
    let timer;
    const tryFocus = () => {
      // 만료됐으면 정리하고 끝 — 나중에 엉뚱하게 반짝이지 않게
      if (expiresAt && Date.now() > expiresAt) {
        setPendingFocus(null);
        return;
      }
      const card = document.querySelector(`[${pendingFocus.attr}="${pendingFocus.value}"]`);
      if (!card) {
        // 충돌 포커스는 이벤트 로드를 기다리며 만료까지 짧게 재시도하고, 끝내 안 보이면(필터로 가려짐 등) 포기한다
        if (expiresAt) timer = setTimeout(tryFocus, FOCUS_RETRY_MS);
        return; // 파라미터 포커스 — events 갱신 시 재시도
      }

      scrollToCard(card);
      card.classList.add('focus-flash');
      setTimeout(() => card.classList.remove('focus-flash'), 1600);
      setPendingFocus(null); // 한 번 플래시했으면 소모
    };
    timer = setTimeout(tryFocus, 400);

    return () => clearTimeout(timer);
  }, [pendingFocus, events]);


  const { searchMembers } = useMemberSearch({
    headBranchId: user?.headBranch?.headBranchId,
    branchId: user?.branch?.branchId,
    token: user?.token,
  });

  // 지점 만료임박 기준 (수강권=잔여 횟수 기준 ticketAlertCount) — 카드/툴팁 임박 뱃지 판정용
  const { data: expirationAlerts } = useQuery({
    queryKey: ['expirationAlerts', user?.branch?.branchId],
    queryFn: () => getExpirationAlerts(user.branch.branchId, user.token),
    enabled: !!user?.branch?.branchId && !!user?.token,
    staleTime: 5 * 60 * 1000,
  });
  const ticketAlertCount = expirationAlerts?.ticketAlertCount ?? null;
  const ticketAlertCountUnit = expirationAlerts?.ticketAlertCountUnit ?? 'FIXED';

  // 설정에서 숨긴 유형을 뺀 이벤트 — 일·주·월 카드, 패널, 칩·강사별 숫자 모두 이것만 본다
  const shownEvents = useMemo(
    () =>
      hiddenClassTypes.length > 0 ? events.filter(event => !hiddenClassTypes.includes(event.courseClassType)) : events,
    [events, hiddenClassTypes],
  );

  // 유형 필터가 적용된 이벤트 — 카운트·화면 모두의 모수가 된다 (사용자 결정: 카운트도 유형에 연동)
  const typedEvents = useMemo(
    () => shownEvents.filter(event => matchesTypeFilter(event, typeFilter)),
    [shownEvents, typeFilter],
  );

  // 헤더 요약 카운트 — 조회 기간(월/주/일)의 건수·예약/출석/결석/대기 인원 (임직원 필터 연동)
  const summaryCounts = useMemo(
    () =>
      summarizeEvents(
        selectedTrainerIds.length > 0
          ? typedEvents.filter(event => selectedTrainerIds.includes(event.trainerId))
          : typedEvents,
        typeFilter,
      ),
    [typedEvents, selectedTrainerIds, typeFilter],
  );

  // 출석 상태 필터 (헤더 요약 칩 토글) — 빈 배열이면 전체 보기.
  // 카운트는 유형 안에서 전체 모수를 보여줘야 필터를 되돌릴 수 있으므로 summaryCounts 는 typedEvents 로 계산한다.
  // 상담·그룹레슨은 출석 상태가 없는 유형이라 상태 필터와 무관하게 항상 표시한다.
  const visibleEvents = useMemo(() => {
    // 칩 필터 = 드롭다운 유형 안에서의 다중선택 표시 필터.
    // attendFilters 는 카테고리 집합이다: 예약/출석/결석(개인·듀엣 상태) + group/consult(유형 통째).
    // 하나라도 선택되면 "선택된 카테고리에 속하는 이벤트만" 보인다 (OR 결합).
    const byCategory =
      attendFilters.length === 0
        ? typedEvents
        : typedEvents.filter(event => {
            if (event.courseClassType === 'GROUP_LESSON') return attendFilters.includes('group');
            if (event.courseClassType === 'CONSULTATION') return attendFilters.includes('consult');
            if (event.courseClassType === 'ETC') return attendFilters.includes('etc');
            // OT: 전체에서는 'ot' 칩으로 통째, 유형=OT 에서는 예약/출석/결석 칩으로 상태별
            if (event.courseClassType === 'OT' && typeFilter !== TYPE_FILTER_OT) return attendFilters.includes('ot');
            // 유형=OT: 여러 명 OT 는 수업 단위 판정 대신 해당 상태 인원이 1명 이상이면 통과
            if (event.courseClassType === 'OT') {
              return attendFilters.some(status => (event[OT_STATUS_COUNT_KEY[status]] || 0) > 0);
            }
            return attendFilters.includes(getLessonAttendStatus(event));
          });

    // 서브 필터는 해당 유형 칩에서만 조작 가능하므로 그 유형일 때만 적용한다 (잔존 필터로 화면이 비는 혼란 방지)
    const byGroupStatus =
      groupStatusFilters.length > 0 && typeFilter === TYPE_FILTER_GROUP
        ? byCategory.filter(
            event =>
              event.courseClassType !== 'GROUP_LESSON' ||
              groupStatusFilters.includes(getGroupReserveStatus(event)),
          )
        : byCategory;

    if (consultResultFilters.length === 0 || typeFilter !== TYPE_FILTER_CONSULT) return byGroupStatus;
    return byGroupStatus.filter(
      event =>
        event.courseClassType !== 'CONSULTATION' ||
        consultResultFilters.includes(event.consultResult || 'SCHEDULED'),
    );
  }, [typedEvents, attendFilters, consultResultFilters, groupStatusFilters, typeFilter]);

  // 유형이 바뀌면 새 유형에서 쓰지 않는 칩 값을 버린다 — 잔존 칩 때문에 화면이 비는 것을 막는다.
  // 전체: 모든 칩, 개인·OT: 예약/출석/결석 칩만, 그룹·상담·기타: 상태 칩 없음.
  const handleTypeFilterChange = next => {
    setTypeFilter(next);
    setAttendFilters(prev => {
      if (next === TYPE_FILTER_ALL) return prev;
      if (next === TYPE_FILTER_PRIVATE || next === TYPE_FILTER_OT) {
        return prev.filter(value => !CATEGORY_CHIP_VALUES.includes(value));
      }
      return [];
    });
  };

  // 드롭다운에서 고른 유형을 설정에서 숨기면 '전체'로 되돌린다
  useEffect(() => {
    const countKey = FILTER_VALUE_TO_COUNT_KEY[typeFilter];
    if (countKey && !savedScheduleSetting.visibleCounts.includes(countKey)) handleTypeFilterChange(TYPE_FILTER_ALL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, savedScheduleSetting.visibleCounts]);

  const handleToggleAttendFilter = status =>
    setAttendFilters(prev => (prev.includes(status) ? prev.filter(item => item !== status) : [...prev, status]));

  // 설정에서 칩을 끄면 그 칩으로 걸어 둔 필터도 푼다 — 보이지 않는 필터로 화면이 비는 것을 막는다
  useEffect(() => {
    setAttendFilters(prev => {
      const next = prev.filter(value => savedScheduleSetting.visibleCounts.includes(FILTER_VALUE_TO_COUNT_KEY[value]));
      return next.length === prev.length ? prev : next;
    });
  }, [savedScheduleSetting.visibleCounts]);

  // 계산된 값 (필터·컬럼·그룹의 공통 소스 = 통합 임직원 목록)
  const filteredTrainers =
    selectedTrainerIds.length > 0 ? staffList.filter(t => selectedTrainerIds.includes(t.trainerId)) : staffList;

  const weekDates = getWeekDates(currentDate, scheduleSetting.weekStartDay);

  // 기간 조회 범위 moment (월간 그리드에 내려줄 안정 참조)
  const rangeMoments = useMemo(
    () => (customRange ? { start: moment(customRange.start), end: moment(customRange.end) } : null),
    [customRange],
  );

  // 여러 명 OT 참석관리 모달에 넘기는 lesson — 렌더마다 새 객체가 되어 자식이 다시 그려지지 않게 고정한다
  const otLesson = useMemo(() => ({ ...modalData, trialTicketList: modalData.trialTickets }), [modalData]);

  // 주간 뷰 기간 조회용 — 범위 내 날짜 배열 (주간 UI를 일수만큼 옆으로 확장, 사용자 확정)
  const rangeDates = useMemo(() => {
    if (!rangeMoments) return null;
    const days = [];
    const cursor = rangeMoments.start.clone();
    while (cursor.isSameOrBefore(rangeMoments.end, 'day')) {
      days.push(cursor.clone());
      cursor.add(1, 'day');
    }
    return days;
  }, [rangeMoments]);

  // 이벤트 핸들러
  // 선택일이 표시 범위(월=그리드, 주=해당 주) 밖이면 보정: 오늘이 범위 안이면 오늘, 아니면 기준일
  const clampSelectedDate = (anchor, targetView) => {
    if (targetView !== 'month' && targetView !== 'week') return;
    const { weekStartDay } = scheduleSetting;
    const start = startOfWeekBy(targetView === 'month' ? anchor.clone().startOf('month') : anchor, weekStartDay);
    const end = endOfWeekBy(targetView === 'month' ? anchor.clone().endOf('month') : anchor, weekStartDay);
    setSelectedDate(prev => {
      if (prev.isBetween(start, end, 'day', '[]')) return prev;
      const today = moment();
      return today.isBetween(start, end, 'day', '[]') ? today : anchor.clone();
    });
  };

  // 주 시작 요일이 바뀌면(미리보기·저장) 표시 주가 달라지므로 선택일이 새 범위 밖이면 보정한다. 기간 조회 중에는 범위가 그대로라 건드리지 않는다
  useEffect(() => {
    if (!customRange) clampSelectedDate(currentDate, view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleSetting.weekStartDay]);

  // 기간 조회 — 선택일이 범위 밖이면 보정 (오늘이 범위 안이면 오늘, 아니면 시작일)
  const clampSelectedToRange = (startM, endM) => {
    setSelectedDate(prev => {
      if (prev.isBetween(startM, endM, 'day', '[]')) return prev;
      const today = moment();
      return today.isBetween(startM, endM, 'day', '[]') ? today : startM.clone();
    });
  };

  // 기간 조회 적용 — 최대 31일. 성공 여부를 돌려줘 헤더가 팝오버를 닫을지 결정한다.
  const handleApplyRange = (startM, endM) => {
    if (!startM?.isValid?.() || !endM?.isValid?.()) {
      toast.error('시작일과 종료일을 모두 선택해주세요');
      return false;
    }
    if (endM.isBefore(startM, 'day')) {
      toast.error('종료일은 시작일 이후여야 합니다');
      return false;
    }
    if (endM.diff(startM, 'days') + 1 > RANGE_MAX_DAYS) {
      toast.error(`조회 기간은 최대 ${RANGE_MAX_DAYS}일까지 선택할 수 있습니다`);
      return false;
    }
    setCustomRange({ start: startM.format('YYYY-MM-DD'), end: endM.format('YYYY-MM-DD') });
    clampSelectedToRange(startM, endM);
    return true;
  };

  const handleClearRange = () => {
    setCustomRange(null);
    // 기간 중 선택한 날짜가 복귀한 뷰 범위 밖이면 보정 — 안 하면 우측 패널이 그리드 밖 날짜로 남는다
    clampSelectedDate(currentDate, view);
  };

  // 날짜 네비게이션 (기간 조회=기간 길이만큼 통째 이동, 그 외 뷰별 단위: 월=±1개월, 주=±1주, 일=±1일)
  const changePeriod = direction => {
    if (customRange) {
      const startM = moment(customRange.start);
      const endM = moment(customRange.end);
      const delta = (endM.diff(startM, 'days') + 1) * (direction === 'inc' ? 1 : -1);
      const nextStart = startM.clone().add(delta, 'days');
      const nextEnd = endM.clone().add(delta, 'days');
      setCustomRange({ start: nextStart.format('YYYY-MM-DD'), end: nextEnd.format('YYYY-MM-DD') });
      clampSelectedToRange(nextStart, nextEnd);
      return;
    }
    const unit = view === 'month' ? 'months' : view === 'week' ? 'weeks' : 'days';
    const nextDate = currentDate.clone().add(direction === 'inc' ? 1 : -1, unit);
    setCurrentDate(nextDate);
    clampSelectedDate(nextDate, view);
  };

  // 날짜 피커로 기준일 직접 변경
  const handleDateChange = dateStr => {
    const nextDate = moment(dateStr);
    setCurrentDate(nextDate);
    clampSelectedDate(nextDate, view);
  };

  const goToToday = () => {
    setCustomRange(null); // 오늘로 돌아가면 기간 조회도 해제하고 원래 뷰로 복귀
    setCurrentDate(moment());
    setSelectedDate(moment());
  };

  // 분할형 패널 푸터 링크: 선택일의 일(강사별) 보기로 이동
  const goToDayView = () => {
    setCurrentDate(selectedDate.clone());
    handleViewChange('day');
  };

  // 강사 필터
  const handleTrainerCheckbox = trainerId => {
    setSelectedTrainerIds(prev =>
      prev.includes(trainerId) ? prev.filter(id => id !== trainerId) : [...prev, trainerId],
    );
  };

  const handleSelectAllTrainers = () => {
    if (selectedTrainerIds.length === staffList.length) {
      setSelectedTrainerIds([]);
    } else {
      setSelectedTrainerIds(staffList.map(t => t.trainerId));
    }
  };

  // 회원 검색
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const getMembers = async (searchValue, overrideMemberType, assignedTrainerId) => {
    const lessonType = modalData?.lessonType || modalData?.courseClassType;
    const isPrivateOrDuet =
      lessonType === 'PRIVATE_LESSON' || lessonType === 'DUET_LESSON' || lessonType === 'OT';
    // 개인·듀엣·OT는 빈 검색어로도 조회한다(포커스 시 전체 목록)
    if ((!searchValue || searchValue.trim().length === 0) && !isPrivateOrDuet) {
      startTransition(() => setSearchResults([]));
      return;
    }
    const memberType = overrideMemberType ||
      (modalData?.lessonType === 'COUNSEL' || modalData?.courseClassType === 'CONSULTATION'
        ? 'ALL'
        : 'MEMBER');
    try {
      const results = await searchMembers(searchValue, memberType, lessonType, assignedTrainerId);
      startTransition(() => setSearchResults(results));
    } catch (error) {
      console.error('회원 검색 중 오류 발생:', error);
    }
  };

  const debouncedGetMembers = useCallback(debounce(getMembers, 100), [
    searchMembers,
    modalData?.lessonType,
    modalData?.courseClassType,
  ]);

  // 상담 팝오버의 회원 이름 클릭 → 회원/잠재회원 상세로 이동.
  // 스케줄 목록 응답에는 memberId·temporaryUserId 가 없어 상담 상세를 한 번 더 읽는다.
  const handleConsultMemberClick = async () => {
    if (!modalData?.consultationId) return;
    try {
      const consultation = await getConsultationById(modalData.consultationId, user.token);
      const path =
        consultation?.consultType === 'MEMBER' && consultation?.memberId
          ? `/member/${consultation.memberId}`
          : consultation?.consultType === 'TEMPORARY' && consultation?.temporaryUserId
            ? `/temporary/${consultation.temporaryUserId}`
            : null;
      if (!path) return;
      setOpens(prev => ({ ...prev, open: false }));
      navigate(path);
    } catch (error) {
      console.error('상담 정보 조회 실패:', error);
    }
  };

  // 개인·듀엣레슨 팝오버 회원명 → 회원상세. 듀엣은 첫 번째 회원(modalData.memberId) 기준.
  const handleLessonMemberClick = () => {
    if (!modalData?.memberId) return;
    setOpens(prev => ({ ...prev, open: false }));
    navigate(`/member/${modalData.memberId}`);
  };

  // 새 일정의 종료 시각·강의실 — 구분별 기본 수업 시간/기본 강의실(지점 스케줄 설정)
  const createDefaultsFor = (lessonType, startHour, startMinute) =>
    getCreateDefaults(savedScheduleSetting, lessonType, startHour, startMinute, rooms);

  // 셀 클릭 (이벤트 추가). startMinutes = 클릭한 칸의 시작 시각(자정부터 분)
  const handleAddEvent = (e, startMinutes, column, targetDate = null) => {
    e.stopPropagation();
    e.preventDefault();
    const hour = Math.floor(startMinutes / 60);
    const minute = startMinutes % 60;

    const dateToUse = targetDate || currentDate;
    const selectedTrainer = staffList[column];
    if (!selectedTrainer) return;

    // 이전 데이터 초기화
    setSelectedMembers([]);
    setSearchResults([]);
    setTickets([]);
    setTrainerId(selectedTrainer.trainerId);
    setTrainerSelectMode(false);

    // 완전히 새로운 modalData로 설정
    setModalData({
      column,
      index: null,
      branchId: user?.branch?.branchId,
      className: '',
      startHour: hour,
      startMinute: minute,
      trainerId: selectedTrainer.trainerId,
      trainerName: selectedTrainer.trainerName,
      registrationStartDate: dateToUse.format('YYYY-MM-DD'),
      registrationEndDate: dateToUse.format('YYYY-MM-DD'),
      exerciseRoomId: -1,
      classTimeInfoList: [],
      courseClassTimeId: null,
      memberId: null,
      memberName: null,
      courseClassId: null,
      memo: '',
      tickets: [],
      consultMethod: '',
      consultPurpose: '',
      consultResult: 'SCHEDULED',
      consultationId: null,
      branchScheduleId: null,
      etcTitle: '',
      courseTicketDeductionCount: 1,
      lessonType: 'PRIVATE_LESSON',
      ...createDefaultsFor('PRIVATE_LESSON', hour, minute),
    });
    setIsUpdate(false);
    setSearchResults([]);
    setOpen(true);
  };

  // 주간 패널 "+ 일정 추가" / 빈칸 클릭 공통 — 담당강사를 비운 채 생성 모달을 연다 (강사는 모달에서 직접 선택)
  const openCreateModalForSchedule = (dateToUse, hour, minute = 0) => {
    setSelectedMembers([]);
    setSearchResults([]);
    setTickets([]);
    setTrainerSelectMode(true);

    setModalData({
      column: null,
      index: null,
      branchId: user?.branch?.branchId,
      className: '',
      startHour: hour,
      startMinute: minute,
      trainerId: null,
      trainerName: '',
      registrationStartDate: dateToUse.format('YYYY-MM-DD'),
      registrationEndDate: dateToUse.format('YYYY-MM-DD'),
      exerciseRoomId: -1,
      classTimeInfoList: [],
      courseClassTimeId: null,
      memberId: null,
      memberName: null,
      courseClassId: null,
      memo: '',
      tickets: [],
      consultMethod: '',
      consultPurpose: '',
      consultResult: 'SCHEDULED',
      consultationId: null,
      branchScheduleId: null,
      etcTitle: '',
      courseTicketDeductionCount: 1,
      lessonType: 'PRIVATE_LESSON',
      ...createDefaultsFor('PRIVATE_LESSON', hour, minute),
    });
    setIsUpdate(false);
    setOpen(true);
  };

  // "+ 일정 추가" 버튼 (헤더·주간/월간 패널 공통) — 보고 있는 날짜 + 영업 시작시간 기본값.
  // 일간은 currentDate 가 보고 있는 날짜이고, 월/주는 패널 선택일(selectedDate)이 기준이다.
  const handlePanelAddSchedule = () => {
    const startHour = savedPeriodTimes?.startTime != null ? Number(savedPeriodTimes.startTime) : 9;
    openCreateModalForSchedule(view === 'day' ? currentDate : selectedDate, startHour);
  };

  // 주간 빈칸 클릭 — 클릭한 날짜/시간으로 생성 (선택일도 함께 변경). startMinutes = 칸 시작 시각(자정부터 분)
  const handleWeekCellCreate = (date, startMinutes) => {
    setSelectedDate(date.clone());
    openCreateModalForSchedule(date, Math.floor(startMinutes / 60), startMinutes % 60);
  };

  // 복사·붙여넣기(#143) — 카드 우클릭 복사, 빈칸 우클릭 붙여넣기.
  // 담당자는 강사 컬럼이 있는 일간만 셀 기준으로 바뀌고, 주간·월간은 원본 담당자를 유지한다.
  // 붙여넣기·드래그가 시간 충돌로 거부되면 부딪힌 카드로 이동해 반짝인다. 다른 날이면 그 날짜로 옮긴 뒤 찾는다.
  // 임직원 필터 등으로 카드가 화면에 없으면 조용히 넘어간다(토스트는 이미 떴다).
  // 드래그 이동·붙여넣기 충돌 토스트 — 상대가 숨긴 유형이면 같은 토스트 안에 줄바꿈으로 안내를 붙인다
  const formatConflictError = (message, conflict) =>
    isHiddenConflict(conflict, hiddenClassTypes) ? withHiddenTypeNote(message) : message;

  const focusConflict = conflict => {
    if (!conflict?.id) return;
    // 숨긴 유형이면 화면에 카드가 없으니 찾지 않는다(안내는 에러 토스트에 이미 붙어 있다)
    if (isHiddenConflict(conflict, hiddenClassTypes)) return;
    const target = conflict.date ? moment(conflict.date, 'YYYY-MM-DD') : null;
    if (target) {
      if (rangeMoments) {
        // 기간 조회는 날짜를 바꾸면 기간 그리드가 깨지므로 범위 안이면 포커스만, 밖이면 안내만 한다
        if (!target.isBetween(rangeMoments.start, rangeMoments.end, 'day', '[]')) {
          toast.info(`충돌한 일정이 조회 기간 밖(${conflict.date})에 있습니다`);
          return;
        }
      } else {
        // 현재 표시 범위(일·주·월) 안이면 기준일을 바꾸지 않는다 — 주는 설정한 주 시작 요일 기준(moment 로케일 주 아님)
        const sameRange =
          view === 'week'
            ? startOfWeekBy(target, scheduleSetting.weekStartDay).isSame(
                startOfWeekBy(currentDate, scheduleSetting.weekStartDay),
                'day',
              )
            : target.isSame(currentDate, view === 'month' ? 'month' : 'day');
        if (!sameRange) {
          setCurrentDate(target);
          // 다른 주·월로 넘어가면 선택일이 새 범위 밖일 수 있어 보정한다
          clampSelectedDate(target, view);
        }
      }
    }
    setPendingFocus({
      attr: conflict.type === 'CONSULTATION' ? 'data-consultation-id' : 'data-class-time-id',
      value: String(conflict.id),
      expiresAt: Date.now() + 5000,
    });
  };

  // 이동·붙여넣기는 날짜를 넘나들므로 현재 범위만이 아니라 이 지점의 캐시된 조회 범위 전부를 무효화한다.
  // 그룹수업 화면의 캐시(['groupLessons', ...])도 같은 일정을 보므로 함께 무효화한다.
  const refreshEvents = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['privateLessons', user?.branch?.branchId] }),
      queryClient.invalidateQueries({ queryKey: ['groupLessons'] }),
    ]);

  // 날짜·시간 변경 확인(출결된 수업 날짜 변경 동의·지난 시간 출결 선택·푸시 여부) — 드래그 이동과 수정 모달 공통
  const moveConfirm = useScheduleMoveConfirm(user);

  const schedulePaste = useSchedulePaste({
    token: user?.token,
    branchId: user?.branch?.branchId,
    toast,
    onPasted: refreshEvents,
    onConflict: focusConflict,
    formatConflictError,
    canPaste: canPrivateEdit,
    // 유형별 수정 권한 — 그룹수업은 SCHEDULE_GROUP_EDIT, 개인·듀엣·OT·상담·기타는 SCHEDULE_PRIVATE_EDIT
    canDelete: event => (event?.courseClassType === 'GROUP_LESSON' ? canGroupEdit : canPrivateEdit),
  });
  const scheduleDelete = useScheduleDelete({ token: user?.token, toast });

  // 붙여넣기 시작 시각 — 정시는 기존처럼 startHour 만, 분이 있는 구간(선택 단위)은 분 단위 startTime 을 함께 넘긴다
  const pasteStartOf = startMinutes => ({
    startHour: Math.floor(startMinutes / 60),
    ...(startMinutes % 60 ? { startTime: toHHmm(startMinutes) } : {}),
  });

  const handleDayCellContextMenu = (e, startMinutes, column) => {
    const trainer = staffList[column];
    schedulePaste.openEmptyMenu(e, {
      date: currentDate.format('YYYY-MM-DD'),
      ...pasteStartOf(startMinutes),
      trainerId: trainer?.trainerId ?? null,
      trainerName: trainer?.trainerName ?? '',
    });
  };

  const handleWeekCellContextMenu = (e, date, startMinutes) =>
    schedulePaste.openEmptyMenu(e, {
      date: date.format('YYYY-MM-DD'),
      ...pasteStartOf(startMinutes),
      trainerId: null,
      trainerName: '',
    });

  const handleMonthCellContextMenu = (e, day) =>
    schedulePaste.openEmptyMenu(e, {
      date: day.format('YYYY-MM-DD'),
      startHour: null,
      trainerId: null,
      trainerName: '',
    });

  // 상세 패널 그룹레슨 행 클릭/이동 pill — 그룹레슨 화면의 해당 수업으로 이동 (시안 4a)
  const handleGroupJump = event => {
    navigate(`/group?focusClassTimeId=${event.courseClassTimeId}&focusDate=${event.registrationStartDate}`);
  };

  // 이벤트 클릭
  const handleEventClick = async (e, event, index, selectedMember = null) => {
    e.stopPropagation();
    e.preventDefault();

    // 그룹레슨은 개인 스케줄에서 조회 전용 (일간 보기 동작과 동일 — 참석관리/수정은 그룹 화면에서)
    if (event.courseClassType === 'GROUP_LESSON') return;

    const registrationStartDate = new Date(event.start);
    const endDate = new Date(event.end);

    setTrainerId(event.trainerId);
    setIsUpdate(true);
    setAnchorEl(e.currentTarget);

    // 기타 일정인 경우 — 회원·수강권이 없어 목록 데이터만으로 수정 폼을 채울 수 있다.
    if (event.courseClassType === 'ETC') {
      setSelectedMembers([]);
      setModalData({
        branchId: user?.branch?.branchId,
        className: event.className,
        etcTitle: event.className,
        registrationStartDate: event.registrationStartDate,
        registrationEndDate: event.registrationEndDate,
        startHour: registrationStartDate.getUTCHours(),
        startMinute: registrationStartDate.getUTCMinutes(),
        endHour: endDate.getUTCHours(),
        endMinute: endDate.getUTCMinutes(),
        column: event.column,
        index,
        trainerId: event.trainerId,
        trainerName: event.trainerName,
        startTime: event.startTime,
        endTime: event.endTime,
        courseClassType: event.courseClassType,
        branchScheduleId: event.branchScheduleId,
        memo: event.memo || '',
        tickets: [],
        lessonType: 'ETC',
      });
      setOpens(prev => ({ ...prev, open: true }));
      return;
    }

    // 상담인 경우
    if (event.courseClassType === 'CONSULTATION') {
      setModalData({
        branchExerciseRoomName: event?.branchExerciseRoomName,
        branchId: user?.branch?.branchId,
        className: event.className,
        consultTitle: event.className, // 상담 제목
        consultType: event?.consultType || 'MEMBER', // 상담 대상 (기본값: 회원)
        registrationStartDate: event.registrationStartDate,
        registrationEndDate: event.registrationEndDate,
        date: event.registrationStartDate, // 상담 날짜
        startHour: registrationStartDate.getUTCHours(),
        startMinute: registrationStartDate.getUTCMinutes(),
        endHour: endDate.getUTCHours(),
        endMinute: endDate.getUTCMinutes(),
        column: event.column,
        index,
        trainerId: event.trainerId,
        trainerName: event.trainerName,
        exerciseRoomId: event.exerciseRoomId,
        memberName: event.memberName,
        memberPhoneNumber: event?.memberPhoneNumber,
        startTime: event.startTime,
        endTime: event.endTime,
        courseClassTimeId: event?.courseClassTimeId,
        memberId: event.memberId,
        courseClassId: event?.courseClassId,
        courseClassType: event?.courseClassType,
        consultationId: event?.consultationId,
        counselManagerId: event?.managerId,
        counselManagerName: event?.managerName,
        consultMethod: event?.consultMethod || '',
        consultPurpose: event?.consultPurpose || '',
        consultResult: event?.consultResult || 'SCHEDULED',
        memo: event?.content || '', // 상담 내용
        content: event?.content || '',
        tickets: [], // 상담은 수강권이 없음
        lessonType: 'COUNSEL', // 상담 타입 설정
      });

      if (event.memberId && event.memberName) {
        const nameOnly = event.memberName;
        setSelectedMembers([{ id: event.memberId, name: nameOnly }]);
      } else {
        setSelectedMembers([]);
      }

      setOpens(prev => ({ ...prev, open: true }));
      return;
    }

    // 레슨 타입
    try {
      const lessonData = await getLessonById(event?.courseClassId, user.token);

      const memberId = selectedMember ? selectedMember.memberId : lessonData?.memberId || null;
      const memberName = selectedMember ? selectedMember.memberName : lessonData?.memberName || '';

      setModalData({
        branchExerciseRoomName: lessonData?.branchExerciseRoomName,
        branchId: lessonData?.branchId,
        className: lessonData.className,
        classDate: event.registrationStartDate, // 클릭한 이벤트의 수업일 (월/주 보기에서 currentDate와 다를 수 있음)
        originalClassDate: event.registrationStartDate, // 수정 모달에서 날짜를 바꿨는지 판단하는 원래 수업일
        registrationStartDate: lessonData.registrationStartDate,
        registrationEndDate: lessonData.registrationEndDate,
        startHour: registrationStartDate.getUTCHours(),
        startMinute: registrationStartDate.getUTCMinutes(),
        endHour: endDate.getUTCHours(),
        endMinute: endDate.getUTCMinutes(),
        column: event.column,
        index,
        trainerId: lessonData.trainerId,
        trainerName: lessonData.trainerName,
        exerciseRoomId: lessonData.branchExerciseRoomId,
        memberName,
        memberPhoneNumber: event?.memberPhoneNumber,
        startTime: event.startTime,
        endTime: event.endTime,
        courseClassTimeId: event?.courseClassTimeId,
        memberId,
        // 듀엣에서 두 번째 회원 행을 눌러 열어도 수정 저장은 수업 대표 회원으로 보낸다
        lessonMemberId: lessonData?.memberId,
        courseClassId: lessonData?.courseClassId,
        membersMaxCount: lessonData?.membersMaxCount,
        courseTicketDeductionCount: lessonData?.courseTicketDeductionCount ?? 1,
        attendedMemberCount: event?.attendedMemberCount,
        reservedMemberCount: event?.reservedMemberCount,
        absentedMemberCount: event?.absentedMemberCount,
        memo: lessonData?.memo,
        tickets: lessonData?.tickets?.map(ticket => ticket.ticketId) || [],
        trialTickets: lessonData?.trialTicketList?.map(ticket => ticket.trialTicketId) || [],
        courseClassType: lessonData?.courseClassType,
        totalSessionCount: event?.totalSessionCount,
        remainingSessionCount: event?.remainingSessionCount,
      });

      if (event?.members?.length > 0) {
        const allMembers = event.members.map(m => ({
          id: m.memberId,
          name: m.memberName || '',
        }));
        setSelectedMembers(allMembers);
      } else if (memberId && memberName) {
        const nameOnly = memberName;
        setSelectedMembers([{ id: memberId, name: nameOnly }]);
      } else {
        setSelectedMembers([]);
      }

      const boundTickets = [
        ...(lessonData?.tickets || []).map(ticket => ({
          ticketType: 'COURSE_TICKET',
          _id: ticket.ticketId,
          optionKey: ticket.memberCourseTicketId ?? ticket.ticketId,
          _name: ticket.ticketName,
        })),
        ...(lessonData?.trialTicketList || []).map(ticket => ({
          ticketType: 'TRIAL_TICKET',
          _id: ticket.trialTicketId,
          optionKey: ticket.memberTrialTicketId ?? ticket.trialTicketId,
          _name: ticket.trialTicketName,
        })),
      ];
      setOriginalTickets(boundTickets);
      setTickets(boundTickets);

      setOpens(prev => ({ ...prev, open: true }));

      // 수정 화면의 수강권 칸에 잔여·만료일을 붙인다 — 수업 상세에는 상품 정보만 있어 참석 회원 응답에서 가져온다.
      // 실패해도 이름만 보이면 되므로 수정은 막지 않는다.
      const requestedClassId = event?.courseClassId;
      editingClassIdRef.current = requestedClassId;
      getTicketsByClassTime(requestedClassId, user.token)
        .then(rows => {
          // 그사이 다른 수업·등록을 열었으면 늦게 온 응답으로 덮어쓰지 않는다
          if (editingClassIdRef.current !== requestedClassId) return;
          const row =
            (rows || []).find(r => r.memberId === lessonData?.memberId && r.status !== 'CANCELED') ||
            (rows || []).find(r => r.status !== 'CANCELED');
          if (!row) return;
          const withPeriod = ticket => ({
            ...ticket,
            remainingCount: row.remainingCount,
            // 표시는 만료일만, 시작일은 날짜 변경 시 기간 검사용
            periodStart: row.ticketStartDate,
            expireDate: row.ticketExpireDate,
            memberId: row.memberId,
            memberName: row.memberName,
            memberCourseTicketId: row.memberCourseTicketId,
          });
          setOriginalTickets(prev => prev.map(withPeriod));
          setTickets(prev => prev.map(withPeriod));
        })
        .catch(error => console.warn('수강권 기간 조회 실패', error));
    } catch (error) {
      console.log(error);
    }
  };

  // 상담 상세 응답을 수정 모달 상태(modalData/selectedMembers)에 반영
  // 스케줄 이벤트 API에는 consultType/temporaryUserId/비회원 정보가 없어 상세 응답으로 덮어써야 한다
  const applyConsultationToModal = useCallback(consultation => {
    if (!consultation) return;

    const { hour: startHour, minute: startMinute } = parseTimeParts(consultation.consultStartTime);
    const { hour: endHour, minute: endMinute } = parseTimeParts(consultation.consultEndTime);
    const consultType = consultation.consultType || 'MEMBER';

    setModalData(prev => ({
      ...prev,
      className: consultation.title,
      consultTitle: consultation.title,
      date: consultation.consultDate,
      registrationStartDate: consultation.consultDate,
      startHour,
      startMinute,
      endHour,
      endMinute,
      consultMethod: consultation.consultMethod,
      consultPurpose: consultation.consultPurpose,
      consultResult: consultation.consultResult || 'SCHEDULED',
      visitPath: consultation.visitPath,
      visitPathEtc: consultation.visitPathEtc,
      exercisePurposes: consultation.exercisePurposes || [],
      exerciseRoomId: consultation.exerciseRoomId ?? -1,
      counselManagerId: consultation.managerId,
      counselManagerName: consultation.managerName,
      memo: consultation.content,
      content: consultation.content,
      consultType,
      // 비회원 정보
      nonMemberName: consultation.name || '',
      nonMemberPhone: consultation.phone || '',
      lessonType: 'COUNSEL',
    }));

    // 회원/잠재회원 정보 설정
    if (consultType === 'NON_MEMBER') {
      setSelectedMembers([]);
    } else if (consultType === 'TEMPORARY') {
      setSelectedMembers(
        consultation.temporaryUserId ? [{ id: consultation.temporaryUserId, name: consultation.name || '' }] : [],
      );
    } else {
      setSelectedMembers(
        consultation.memberId ? [{ id: consultation.memberId, name: consultation.memberName || '' }] : [],
      );
    }
  }, []);

  // 모달 저장이 시간 충돌로 실패하면 입력 보존을 위해 모달을 열어 둔 채 충돌 정보만 기억한다
  const pendingConflictRef = useRef(null);
  // 숨긴 유형과의 충돌은 이동할 카드가 없으니 기억하지 않고, 문구에 숨김 안내를 붙인다
  const rememberConflict = conflict => {
    pendingConflictRef.current = conflict?.id && !isHiddenConflict(conflict, hiddenClassTypes) ? conflict : null;
  };
  const withConflictHint = (message, conflict) => {
    if (!conflict?.id) return message;
    if (isHiddenConflict(conflict, hiddenClassTypes)) return withHiddenTypeNote(message);
    return `${message} (닫으면 겹친 일정으로 이동합니다)`;
  };

  // 이벤트 저장
  const handleSaveEvent = async () => {
    if (isUpdate) {
      try {
        const targetCourseClassType = lessonTypeToClassType(modalData.lessonType || modalData.courseClassType);
        const pad2 = value => String(value ?? 0).padStart(2, '0');
        const newStartTime = `${pad2(modalData?.startHour)}:${pad2(modalData?.startMinute)}`;
        const newEndTime = `${pad2(modalData?.endHour)}:${pad2(modalData?.endMinute)}`;
        const decision = await moveConfirm.ask({
          courseClassId: modalData.courseClassId,
          from: { date: modalData.originalClassDate, startTime: modalData.startTime, endTime: modalData.endTime },
          to: { date: modalData.classDate, startTime: newStartTime, endTime: newEndTime },
        });
        if (!decision) return null;
        await withOverlapConfirm(force => updateLesson(
          {
            ...modalData,
            // 수정 PUT 의 회원은 클릭한 회원 행이 아니라 수업 대표 회원이다(아니면 406)
            memberId: modalData.lessonMemberId ?? modalData.memberId,
            branchId: user.branch?.branchId,
            registrationEndDate: modalData?.registrationEndDate,
            courseClassType: targetCourseClassType,
            courseTicketDeductionCount: Number(modalData.courseTicketDeductionCount) || 1,
            tickets: modalData.tickets || [],
            trialTickets: modalData.trialTickets || [],
            lessonUpdateType: 'all',
            // 수업일을 명시해 보낸다 — 등록일·요일 조합 판정에 맡기면 일괄생성 회차가 옛 등록일로 못 옮겨진다
            classDate: modalData?.classDate,
            pastAttendanceStatus: decision.pastAttendanceStatus,
            classTimeInfoList: [
              {
                // 월/주 보기에서는 클릭한 이벤트 날짜 기준 (currentDate는 조회 앵커일 뿐)
                dayOfWeek: getDayOfWeek(modalData?.classDate || currentDate),
                startTime: newStartTime,
                endTime: newEndTime,
              },
            ],
          },
          user.token,
          force,
        ));
        await moveConfirm.notify(decision, {
          branchId: user.branch?.branchId,
          trainerName: modalData.trainerName,
          className: modalData.className,
          date: modalData.classDate,
          startTime: newStartTime,
        });
        rememberConflict(null);
        const successMsg = t('Class updated successfully');
        await refetchEvents();
        handleCancel();
        toast.success(successMsg);
        return;
      } catch (error) {
        // 겹침 확인을 취소했으면 앞선 시도의 겹친 일정으로 이동하지 않게 지운다
        if (error?.silent) {
          rememberConflict(null);
          return null;
        }
        rememberConflict(error?.response?.data?.conflict);
        toast.error(withConflictHint(errorMessages(error), error?.response?.data?.conflict));
        // 이동 커밋 뒤 출결 실패일 수 있으니 목록은 새로 고친다(입력 보존을 위해 모달은 열어 둔다)
        refetchEvents();
        return null;
      }
    } else {
      try {
        const memberId = selectedMembers.length > 0 ? selectedMembers[0].id : modalData.memberId;
        const targetCourseClassType = lessonTypeToClassType(modalData.lessonType);
        // OT: 회원 전원을 생성과 동시에 예약. 수업↔상품 연결은 선택 회원들의 OT 수강권/일일권 합집합.
        const isOt = targetCourseClassType === 'OT';
        // OT: 회원마다 모달에서 고른 자기 수강권/일일권으로 예약·차감한다.
        const otPicked = isOt
          ? selectedMembers.map(m => ({
              member: m,
              ticket: (otMemberTickets[m.id] || []).find(
                ticket => otTicketValue(ticket) === modalData.otTicketSelections?.[m.id],
              ),
            }))
          : [];
        if (isOt) {
          if (selectedMembers.some(m => otMemberTickets[m.id] === undefined && !otTicketErrors[m.id])) {
            toast.error('수강권 조회 중입니다. 잠시 후 다시 시도해 주세요');
            return null;
          }
          const failed = selectedMembers.filter(m => otTicketErrors[m.id]);
          if (failed.length > 0) {
            toast.error(`수강권 조회에 실패한 회원이 있습니다. 다시 시도해 주세요: ${failed.map(m => m.name).join(', ')}`);
            return null;
          }
          const wanted = Number(modalData.membersMaxCount) || 1;
          // 회원은 1명 이상 인원 수 이하 — 정원보다 적게 만들어도 된다(사용자 확정 2026-09-29)
          if (selectedMembers.length === 0 || selectedMembers.length > wanted) {
            toast.error(`회원을 1명 이상 인원 수(${wanted}명) 이하로 선택해 주세요`);
            return null;
          }
          const noTicket = selectedMembers.filter(m => !(otMemberTickets[m.id] || []).length);
          if (noTicket.length > 0) {
            toast.error(`OT 수강권이 없는 회원이 있습니다: ${noTicket.map(m => m.name).join(', ')}`);
            return null;
          }
          const notPicked = otPicked.filter(p => !p.ticket);
          if (notPicked.length > 0) {
            toast.error(`수강권을 선택해 주세요: ${notPicked.map(p => p.member.name).join(', ')}`);
            return null;
          }
        }
        const otCourse = otPicked.map(p => p.ticket).filter(ticket => ticket.ticketType !== 'TRIAL_TICKET');
        const otTrial = otPicked.map(p => p.ticket).filter(ticket => ticket.ticketType === 'TRIAL_TICKET');
        const otPayload = isOt
          ? {
              memberIds: selectedMembers.map(m => m.id),
              membersMaxCount: Number(modalData.membersMaxCount) || 1,
              tickets: [...new Set(otCourse.map(ticket => ticket.courseTicketId).filter(id => id != null))],
              trialTickets: [...new Set(otTrial.map(ticket => ticket.trialTicketId).filter(id => id != null))],
              memberCourseTicketIds: otCourse.map(ticket => ticket.memberCourseTicketId),
              memberTrialTicketIds: otTrial.map(ticket => ticket.memberTrialTicketId),
              memberCourseTicketId: null,
              memberTrialTicketId: null,
            }
          : {};
        // 듀엣: 고른 회원(1~2명)만 예약한다 — 상대를 강제하지 않는다(11/3). 수강권을 고른 회원을 앞에 둔다.
        // 그 회원을 뺐으면 고른 수강권은 보내지 않고 회원마다 듀엣 수강권을 서버가 고른다.
        const duetOwnerIn = selectedMembers.some(m => m.id === modalData.memberId);
        const duetMemberIds = duetOwnerIn
          ? [modalData.memberId, ...selectedMembers.map(m => m.id).filter(id => id !== modalData.memberId)]
          : selectedMembers.map(m => m.id);
        const duetPayload =
          targetCourseClassType === 'DUET_LESSON' && duetMemberIds.length > 0
            ? {
                memberIds: duetMemberIds,
                memberId: duetMemberIds[0],
                ...(duetOwnerIn ? {} : { memberCourseTicketId: null, memberTrialTicketId: null }),
              }
            : {};
        await withOverlapConfirm(force => createLesson(
          {
            ...modalData,
            // 인원 수는 OT 에서만 의미가 있다. 개인은 1 고정, 듀엣은 기존 값을 그대로 보낸다.
            membersMaxCount: targetCourseClassType === 'PRIVATE_LESSON' ? 1 : modalData.membersMaxCount,
            memberId,
            branchId: user.branch?.branchId,
            memo: modalData.memo || '',
            courseClassType: targetCourseClassType,
            courseTicketDeductionCount: Number(modalData.courseTicketDeductionCount) || 1,
            tickets: modalData.tickets || [],
            trialTickets: modalData.trialTickets || [],
            ...otPayload,
            ...duetPayload,
            classTimeInfoList: [
              {
                dayOfWeek: getDayOfWeek(modalData?.registrationStartDate),
                startTime: `${modalData?.startHour.toString().padStart(2, '0')}:${modalData?.startMinute.toString().padStart(2, '0')}`,
                endTime: `${modalData?.endHour.toString().padStart(2, '0')}:${modalData?.endMinute.toString().padStart(2, '0')}`,
              },
            ],
          },
          user.token,
          force,
        ));
        rememberConflict(null);
        const successMsg = t('Class created successfully');
        await refetchEvents();
        handleCancel();
        toast.success(successMsg);
        return;
      } catch (error) {
        if (error?.silent) {
          rememberConflict(null);
          return null;
        }
        rememberConflict(error?.response?.data?.conflict);
        toast.error(withConflictHint(errorMessages(error), error?.response?.data?.conflict));
        return null;
      }
    }
  };

  // 취소/닫기
  const handleCancel = () => {
    // 저장 충돌로 기억해 둔 카드가 있으면 모달을 닫는 순간 그리로 이동한다
    const pending = pendingConflictRef.current;
    pendingConflictRef.current = null;
    if (pending) focusConflict(pending);
    setIsUpdate(false);
    setOpen(false);
    setOpens({ open: false, open1: false, open2: false, open3: false });
    setAnchorEl(null);
    setSelectedMembers([]);
    setOtMemberTickets({});
    setOtTicketErrors({});
    setSearchResults([]);
    setIsMemberChanged(false);
    setTickets([]);
    setTrainerSelectMode(false);
    setModalData({
      className: '',
      registrationStartDate: currentDate.toISOString().split('T')[0],
      registrationEndDate: currentDate.toISOString().split('T')[0],
      startHour: null,
      startMinute: 0,
      endHour: null,
      endMinute: 50,
      column: null,
      index: null,
      trainerId,
      trainerName: '',
      exerciseRoomId: -1,
      branchExerciseRoomName: '',
      memberName: '',
      memberId: null,
      startTime: '',
      endTime: '',
      courseClassTimeId: null,
      courseClassId: null,
      membersMaxCount: 0,
      courseTicketDeductionCount: 1,
      memo: '',
      tickets: [],
      trialTickets: [],
    });
  };

  // 수정 모달 수강권 잔여·만료일 조회가 늦게 왔을 때 지금 열린 수업 것인지 확인하는 용도
  const editingClassIdRef = useRef(null);
  const movingRef = useRef(false);

  // 일정 이동 저장 — 일간은 시간만, 주간·월간은 날짜(와 시간)를 바꾼다.
  // newDate('YYYY-MM-DD')가 없으면 날짜는 그대로다(일간 드래그). newTrainer({trainerId, trainerName})가 없으면 강사는 그대로다.
  // 개인·듀엣·OT 이동은 trainerAssignMode('ADD'|'REPLACE')가 있을 때만 회원 수강권 담당강사를 바꾼다(없으면 수업 강사만 교체).
  // 개인·듀엣·OT 는 단일 날짜 수업이라 등록 시작·종료일을 새 날짜로 함께 옮긴다.
  const saveScheduleMove = async (event, { newDate, newStartTime, newEndTime, newTrainer, trainerAssignMode }) => {
    // 저장에 쓰는 날짜 — 옮길 날짜가 없으면(일간 드래그) 카드가 놓여 있는 현재 수업일이다.
    // 상세(GET)의 등록일은 옛 값일 수 있어 PUT 에 그대로 실으면 날짜가 되돌아갈 수 있다.
    const targetDate = newDate || event.registrationStartDate;
    // 진행 중이면 재진입하지 않는다(모달 [이동] 연타·연속 드래그로 같은 카드가 두 번 저장되는 것 방지)
    if (movingRef.current) return;
    movingRef.current = true;
    const movedLabel = moment(newDate || event.registrationStartDate, 'YYYY-MM-DD').format('M월 D일');
    const successMessage = `일정을 ${movedLabel} ${newStartTime} 로 옮겼습니다`;

    try {
      // 기타 드래그 — PUT 이 전체 교체 방식이라 GET 원본 위에 날짜·시간·담당자만 바꿔 보낸다(목록 스냅샷으로 덮어쓰지 않는다).
      if (event.courseClassType === 'ETC') {
        const schedule = await getBranchScheduleById(event.branchScheduleId, user.token);
        await updateBranchSchedule(
          event.branchScheduleId,
          {
            branchId: schedule.branchId ?? user?.branch?.branchId,
            employeeId: newTrainer ? newTrainer.trainerId : schedule.employeeId,
            employeeName: newTrainer ? newTrainer.trainerName : schedule.employeeName,
            title: schedule.title,
            scheduleDate: targetDate || schedule.scheduleDate,
            startTime: newStartTime,
            endTime: newEndTime,
            content: schedule.content ?? null,
          },
          user.token,
        );
        toast.success(successMessage);
        await refreshEvents();
        return;
      }

      // 상담 드래그 — PUT 이 전체 교체 방식이라 GET 원본 위에 시간만 바꿔 보낸다.
      // 목록 정규화에서 managerId→trainerId 로 바뀌므로 저장은 반드시 GET 응답의 managerId 를 쓴다.
      if (event.courseClassType === 'CONSULTATION') {
        const consultation = await getConsultationById(event.consultationId, user.token);
        await withOverlapConfirm(force => updateConsultation(
          event.consultationId,
          {
            consultType: consultation.consultType || 'MEMBER',
            memberId: consultation.memberId,
            temporaryUserId: consultation.temporaryUserId,
            name: consultation.name,
            phone: consultation.phone,
            branchId: consultation.branchId,
            title: consultation.title,
            consultDate: targetDate || consultation.consultDate,
            consultStartTime: newStartTime,
            consultEndTime: newEndTime,
            consultMethod: consultation.consultMethod,
            consultPurpose: consultation.consultPurpose,
            consultResult: consultation.consultResult,
            visitPath: consultation.visitPath,
            visitPathEtc: consultation.visitPathEtc,
            exercisePurposes: consultation.exercisePurposes,
            // 목록의 trainerId 는 상담에선 managerId(Employee ID 공간)라 새 강사 컬럼 값을 그대로 쓴다
            managerId: newTrainer ? newTrainer.trainerId : consultation.managerId,
            managerName: newTrainer ? newTrainer.trainerName : consultation.managerName,
            content: consultation.content,
          },
          user.token,
          force,
        ));
        toast.success(successMessage);
      } else {
        const lessonData = await getLessonById(event.courseClassId, user.token);
        const targetCourseClassType = lessonTypeToClassType(lessonData.courseClassType);
        const moveDate = targetDate || currentDate.format('YYYY-MM-DD');
        const decision = await moveConfirm.ask({
          courseClassId: event.courseClassId,
          from: { date: event.registrationStartDate, startTime: event.startTime, endTime: event.endTime },
          to: { date: moveDate, startTime: newStartTime, endTime: newEndTime },
        });
        if (!decision) return;

        await withOverlapConfirm(force => updateLesson(
          {
            courseClassId: lessonData.courseClassId,
            className: lessonData.className,
            trainerId: newTrainer ? newTrainer.trainerId : lessonData.trainerId,
            memberId: lessonData.memberId,
            branchExerciseRoomId: lessonData.branchExerciseRoomId,
            registrationStartDate: targetDate || lessonData.registrationStartDate,
            registrationEndDate: targetDate || lessonData.registrationEndDate,
            membersMaxCount: lessonData.membersMaxCount,
            courseTicketDeductionCount: lessonData.courseTicketDeductionCount,
            memo: lessonData.memo || '',
            tickets: lessonData.tickets?.map(ticket => ticket.ticketId) || [],
            trialTickets: lessonData.trialTicketList?.map(ticket => ticket.trialTicketId) || [],
            branchId: user.branch?.branchId,
            courseClassType: targetCourseClassType,
            lessonUpdateType: 'all',
            classDate: moveDate,
            pastAttendanceStatus: decision.pastAttendanceStatus,
            ...(trainerAssignMode ? { trainerAssignMode } : {}),
            classTimeInfoList: [
              {
                dayOfWeek: dayOfWeekName(targetDate || currentDate.format('YYYY-MM-DD')),
                startTime: newStartTime,
                endTime: newEndTime,
              },
            ],
          },
          user.token,
          force,
        ));
        toast.success(successMessage);
        await moveConfirm.notify(decision, {
          branchId: user.branch?.branchId,
          trainerName: newTrainer ? newTrainer.trainerName : lessonData.trainerName,
          className: lessonData.className,
          date: moveDate,
          startTime: newStartTime,
        });
      }
      await refreshEvents();
    } catch (error) {
      if (error?.silent) return;
      toast.error(formatConflictError(errorMessages(error), error?.response?.data?.conflict));
      focusConflict(error?.response?.data?.conflict);
      // 지난 시간 이동은 이동이 커밋된 뒤 출결에서 실패할 수 있다 — 카드가 옛 자리에 남지 않게 다시 불러온다
      refreshEvents();
    } finally {
      movingRef.current = false;
    }
  };

  // 드래그로 놓기(일·주·월 공통) — 바로 저장하지 않고 이동/복사를 묻는 모달을 띄운다. 취소하면 카드는 그대로다.
  const [dropAsk, setDropAsk] = useState(null);

  // 일간 — 놓은 시각(분 단위) 그대로. targetTrainer({trainerId, trainerName})가 있으면 다른 강사 컬럼에 놓은 것이다.
  const handleDragTimeChange = (event, newStartHour, newStartMinute, targetTrainer) => {
    const duration = event.endHour * 60 + event.endMinute - (event.startHour * 60 + event.startMinute);
    const newEndTotalMinutes = newStartHour * 60 + newStartMinute + duration;
    const toTime = (hour, minute) => `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    setDropAsk({
      event,
      mode: 'day',
      newTrainer: targetTrainer || null,
      newDate: currentDate.format('YYYY-MM-DD'),
      startTime: toTime(newStartHour, newStartMinute),
      endTime: toTime(Math.floor(newEndTotalMinutes / 60), newEndTotalMinutes % 60),
    });
  };

  // 주간 — 같은 날·같은 구간이면 무시. 구간이 원래와 다르면 '시간도 변경'이 기본 켜짐(시작=놓은 구간 시작 시각).
  // 구간 = 선택 단위(5분~1시간). slotMinutes 는 놓은 구간의 시작 시각(자정부터 분).
  const handleWeekEventDrop = (event, date, slotMinutes) => {
    const newDate = date.format('YYYY-MM-DD');
    const sameStart = isSameStart(event, slotMinutes, snapMinutes);
    if (newDate === event.registrationStartDate && sameStart) return;
    setDropAsk({
      event,
      mode: 'week',
      newDate,
      defaultWithTime: !sameStart,
      defaultStart: sameStart ? event.startTime : toHHmm(slotMinutes),
    });
  };

  // 월간 — 같은 날이면 무시. '시간도 변경'은 기본 꺼짐(시작=원래 시각).
  const handleMonthEventDrop = (event, day) => {
    const newDate = day.format('YYYY-MM-DD');
    if (newDate === event.registrationStartDate) return;
    setDropAsk({ event, mode: 'month', newDate, defaultWithTime: false, defaultStart: event.startTime });
  };

  // 일간 강사 변경 이동에서 새 강사가 담당강사가 아닐 때 묻는 모달 상태 — { event, move, targets }
  const [moveAssignAsk, setMoveAssignAsk] = useState(null);
  const moveCheckingRef = useRef(false);

  const confirmDropMove = async ({ newDate, newStartTime, newEndTime }) => {
    if (!dropAsk || moveCheckingRef.current) return;
    const { event, mode, newTrainer } = dropAsk;
    setDropAsk(null);
    // 일간은 날짜를 바꾸지 않는다(newDate 생략)
    const move = {
      newDate: mode === 'day' ? undefined : newDate,
      newStartTime,
      newEndTime,
      newTrainer: newTrainer || undefined,
    };

    // 일간에서 개인·듀엣·OT 를 다른 강사 컬럼으로 옮기면 새 강사가 수강권 담당강사인지 먼저 확인한다.
    // 확인 실패면 이동하지 않는다(카드는 원래 자리).
    if (mode === 'day' && newTrainer && ['PRIVATE_LESSON', 'DUET_LESSON', 'OT'].includes(event.courseClassType)) {
      moveCheckingRef.current = true;
      try {
        const check = await checkMoveTrainerAssignment({
          courseClassId: event.courseClassId,
          trainerId: newTrainer.trainerId,
          token: user?.token,
        });
        if (check?.needsAssign) {
          setMoveAssignAsk({ event, move, targets: check.targets || [] });
          return;
        }
      } catch (error) {
        toast.error(errorMessages(error));
        return;
      } finally {
        moveCheckingRef.current = false;
      }
    }

    saveScheduleMove(event, move);
  };

  // mode = 'ADD' | 'REPLACE' | null(담당강사 그대로 이동)
  const confirmMoveAssign = mode => {
    if (!moveAssignAsk) return;
    const { event, move } = moveAssignAsk;
    setMoveAssignAsk(null);
    saveScheduleMove(event, { ...move, trainerAssignMode: mode || undefined });
  };

  const confirmDropCopy = ({ newDate, newStartTime }) => {
    const { event, newTrainer } = dropAsk;
    setDropAsk(null);
    const label = moment(newDate, 'YYYY-MM-DD').format('M월 D일');
    // 강사를 바꿨으면 그 강사로, 아니면 원래 강사(trainerId 생략) — 붙여넣기와 같은 흐름(담당강사 추가 확인 포함)으로 새 일정을 만든다
    schedulePaste.copyEventTo(
      event,
      {
        date: newDate,
        startTime: newStartTime,
        trainerId: newTrainer ? newTrainer.trainerId : null,
        trainerName: newTrainer ? newTrainer.trainerName : '',
      },
      `일정을 ${label} ${newStartTime} 에 복사했습니다`,
    );
  };

  // 수업 삭제
  const deleteWeek = async () => {
    try {
      await deleteWeekById(modalData?.courseClassTimeId, user.token);
      handleCancel();
      await refetchEvents();
    } catch (err) {
      console.log(err);
      toast.error('사용자 예약이 돼있어서 삭제 불가능합니다');
      handleCancel();
    }
  };

  // 이벤트 삭제 핸들러
  const handleEventDeleted = courseClassTimeId => {
    refetchEvents();
  };

  // 회원 변경 핸들러 - 선택한 회원이 소지한 수강권만 조회
  const handleChangeMember = async (memberId, memberName) => {
    // OT 는 회원별 수강권을 아래 effect 가 모아 온다(단일 수강권 select 없음).
    if (modalData.lessonType === 'OT') return;
    if (memberId && memberId !== -1) {
      try {
        const targetLessonType = lessonTypeToClassType(modalData.lessonType);

        // 선택한 회원의 수강권 조회
        const queryString = QueryString.stringify({
          branchId: user?.branch?.branchId,
          courseClassType: targetLessonType,
        });
        const response = await getUserTickets(memberId, user.token, queryString);

        const filteredTickets = normalizeMemberTickets(response, targetLessonType);

        setOriginalTickets(filteredTickets);
        setTickets(filteredTickets);

        setModalData(prev => ({
          ...prev,
          memberId,
          memberName,
          tickets: [],
          trialTickets: [],
          memberCourseTicketId: null,
          memberTrialTicketId: null,
        }));
      } catch (error) {
        toast.error(errorMessages(error));
      }
    } else {
      setTickets([]);
      setModalData(prev => ({
        ...prev,
        tickets: [],
        trialTickets: [],
        memberCourseTicketId: null,
        memberTrialTicketId: null,
      }));
    }
  };

  // ============================================
  // Effects
  // ============================================

  // OT 회원 수강권 조회. 실패는 빈 목록으로 두지 않고 otTicketErrors 에 표시한다.
  const fetchOtTickets = ids =>
    Promise.all(
      ids.map(id =>
        getUserTickets(id, user.token, QueryString.stringify({ branchId: user?.branch?.branchId, courseClassType: 'OT' }))
          .then(response => [id, normalizeMemberTickets(response, 'OT'), false])
          .catch(() => [id, [], true]),
      ),
    );

  // OT: 선택 회원마다 OT 수강권/일일권을 모아 둔다(생성 시 회원 전원 자동 예약·각자 차감).
  useEffect(() => {
    if (!open || isUpdate || modalData.lessonType !== 'OT') {
      if (Object.keys(otMemberTickets).length > 0) setOtMemberTickets({});
      if (Object.keys(otTicketErrors).length > 0) setOtTicketErrors({});
      return;
    }
    const selectedIds = selectedMembers.map(m => m.id);
    const missing = selectedIds.filter(id => !otMemberTickets[id]);
    const stale = Object.keys(otMemberTickets).filter(id => !selectedIds.includes(id));
    if (missing.length === 0 && stale.length === 0) return;

    let cancelled = false;
    fetchOtTickets(missing).then(entries => {
      if (cancelled) return;
      setOtMemberTickets(prev => {
        const next = {};
        selectedIds.forEach(id => {
          if (prev[id]) next[id] = prev[id];
        });
        entries.forEach(([id, list]) => {
          next[id] = list;
        });
        return next;
      });
      setOtTicketErrors(prev => {
        const next = {};
        selectedIds.forEach(id => {
          if (prev[id]) next[id] = true;
        });
        entries.forEach(([id, , failed]) => {
          if (failed) next[id] = true;
          else delete next[id];
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isUpdate, modalData.lessonType, selectedMembers]);

  // 조회 실패한 회원의 수강권만 다시 조회한다
  const retryOtTickets = async id => {
    setOtTicketErrors(prev => ({ ...prev, [id]: false }));
    setOtMemberTickets(prev => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
    const [[, list, failed]] = await fetchOtTickets([id]);
    setOtMemberTickets(prev => ({ ...prev, [id]: list }));
    setOtTicketErrors(prev => ({ ...prev, [id]: failed }));
  };

  // 레슨 타입 변경 시 선택된 회원의 수강권 다시 조회
  useEffect(() => {
    // 상담 타입이면 수강권 조회 안함
    if (modalData.lessonType === 'COUNSEL') {
      setTickets([]);
      return;
    }

    // OT 는 회원별 수강권 effect 가 따로 처리한다
    if (modalData.lessonType === 'OT') {
      setTickets([]);
      return;
    }

    // 회원이 선택되어 있으면 해당 회원의 수강권 조회
    if (open && selectedMembers.length > 0 && !isUpdate) {
      const memberId = selectedMembers[0]?.id;
      if (memberId) {
        const targetType = lessonTypeToClassType(modalData.lessonType);
        const queryString = QueryString.stringify({
          branchId: user?.branch?.branchId,
          courseClassType: targetType,
        });

        getUserTickets(memberId, user.token, queryString)
          .then(response => {
            const filteredTickets = normalizeMemberTickets(response, targetType);
            setOriginalTickets(filteredTickets);
            setTickets(filteredTickets);
          })
          .catch(error => {
            console.error('수강권 조회 오류:', error);
            setTickets([]);
            setModalData(prev => ({
              ...prev,
              tickets: [],
              trialTickets: [],
              memberCourseTicketId: null,
              memberTrialTicketId: null,
            }));
          });
      }
    } else if (open && selectedMembers.length === 0 && !isUpdate) {
      setTickets([]);
      setModalData(prev => ({
        ...prev,
        tickets: [],
        trialTickets: [],
        memberCourseTicketId: null,
        memberTrialTicketId: null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalData.lessonType, open, isUpdate, selectedMembers.length]);

  // 수정 모드
  useEffect(() => {
    if (isUpdate) {
      const selectedMember = { id: modalData.memberId, name: modalData.memberName };
      setSearchResults(prev => {
        if (!prev.find(option => option.id === modalData.memberId)) {
          return [selectedMember];
        }
        return prev;
      });
    }
  }, [isUpdate, modalData?.memberId, modalData?.memberName]);

  // 수정 모드에서 회원 변경 시 회원의 수강권 조회 (첫 진입은 lessonData 결합 티켓 유지)
  useEffect(() => {
    if (
      open &&
      isUpdate &&
      isMemberChanged &&
      selectedMembers.length > 0 &&
      modalData.courseClassType !== 'CONSULTATION'
    ) {
      const memberId = selectedMembers[0]?.id;
      if (memberId) {
        const targetType = lessonTypeToClassType(modalData.courseClassType);
        const queryString = QueryString.stringify({
          branchId: user?.branch?.branchId,
          courseClassType: targetType,
        });

        getUserTickets(memberId, user.token, queryString)
          .then(response => {
            const filteredTickets = normalizeMemberTickets(response, targetType);
            setOriginalTickets(filteredTickets);
            setTickets(filteredTickets);
          })
          .catch(error => {
            console.error('수강권 조회 오류:', error);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isUpdate, isMemberChanged, selectedMembers.length]);

  // 시간 설정 — 일간 행은 표 칸 단위(30분/1시간/2시간) 칸. 범위는 기존과 같이 시작 시 ~ 종료 시 행까지.
  // 1시간 단위는 종료 24시면 24:00 행(경계 라벨)까지 그대로 두고, 그 외 단위는 24:00 이후 행을 만들지 않는다.
  // 범위가 칸 단위로 안 나눠지면(2시간 칸) 마지막 행은 남은 길이만큼이다. 행 = { label, startMinutes, minutes }
  const { timeUnitMinutes } = scheduleSetting;
  useEffect(() => {
    if (!periodTimes) return;
    const startHour = Number(periodTimes?.startTime);
    const rangeEnd = (Number(periodTimes?.endTime) + 1) * 60;
    const lastMinutes = timeUnitMinutes === 60 ? rangeEnd : Math.min(rangeEnd, 24 * 60);
    const rows = [];
    for (let startMinutes = startHour * 60; startMinutes < lastMinutes; startMinutes += timeUnitMinutes) {
      rows.push({
        label: toHHmm(startMinutes),
        startMinutes,
        minutes: Math.min(timeUnitMinutes, lastMinutes - startMinutes),
      });
    }
    setHours(rows);
  }, [periodTimes, timeUnitMinutes]);

  // ============================================
  // 임직원 표시 순서 변경 (강사·비강사 공통)
  // ============================================
  const invalidateStaffQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['trainers', user?.branch?.branchId] });
    queryClient.invalidateQueries({ queryKey: ['branchManagers', user?.branch?.branchId] });
  };

  const updateTrainerOrderMutation = useMutation({
    mutationFn: staffIds => updateBranchTrainerOrder(user?.branch?.branchId, staffIds, user?.token),
    onSuccess: invalidateStaffQueries,
    onError: error => {
      toast.error(errorMessages(error));
      // 실패 시 서버 순서 다시 가져옴 (옵티미스틱 업데이트 롤백)
      invalidateStaffQueries();
    },
  });

  const applyStaffOrder = newStaffList => {
    // 옵티미스틱 업데이트 (즉시 화면 반영) — staffList가 sortOrder로 정렬되므로 두 원본 쿼리의 sortOrder를 갱신한다.
    // 실패 시 onError에서 invalidate로 롤백.
    const nextSortOrders = new Map(newStaffList.map((staff, index) => [staff.trainerId, index]));
    queryClient.setQueryData(['trainers', user?.branch?.branchId], prev =>
      prev?.map(trainer => ({ ...trainer, sortOrder: nextSortOrders.get(trainer.trainerId) ?? trainer.sortOrder })),
    );
    queryClient.setQueryData(['branchManagers', user?.branch?.branchId], prev =>
      prev?.map(manager => ({ ...manager, sortOrder: nextSortOrders.get(manager.employeeId) ?? manager.sortOrder })),
    );

    updateTrainerOrderMutation.mutate(newStaffList.map(staff => staff.trainerId));
  };

  // 화살표 이동 — 옆 컬럼과 자리 교환
  const handleTrainerMove = (staffId, direction) => {
    const currentIndex = staffList.findIndex(staff => staff.trainerId === staffId);
    if (currentIndex === -1) return;

    const swapIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    if (swapIndex < 0 || swapIndex >= staffList.length) return;

    const newStaffList = [...staffList];
    [newStaffList[currentIndex], newStaffList[swapIndex]] = [newStaffList[swapIndex], newStaffList[currentIndex]];

    applyStaffOrder(newStaffList);
  };

  // 드래그 이동 — 끌어온 컬럼을 놓은 위치로 삽입(사이의 컬럼들은 한 칸씩 밀림)
  const handleTrainerReorder = (sourceStaffId, targetStaffId) => {
    const fromIndex = staffList.findIndex(staff => staff.trainerId === sourceStaffId);
    const toIndex = staffList.findIndex(staff => staff.trainerId === targetStaffId);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const newStaffList = [...staffList];
    const [movedStaff] = newStaffList.splice(fromIndex, 1);
    newStaffList.splice(toIndex, 0, movedStaff);

    applyStaffOrder(newStaffList);
  };

  // ============================================
  // 렌더링
  // ============================================
  return (
    <>
      <PrivateLessonHeader
        view={view}
        currentDate={currentDate}
        weekDates={weekDates}
        selectedTrainerIds={selectedTrainerIds}
        summaryCounts={summaryCounts}
        attendFilters={attendFilters}
        onToggleAttendFilter={handleToggleAttendFilter}
        consultResultFilters={consultResultFilters}
        onToggleConsultResultFilter={handleToggleConsultResultFilter}
        groupStatusFilters={groupStatusFilters}
        onToggleGroupStatusFilter={handleToggleGroupStatusFilter}
        onResetFilters={handleResetFilters}
        typeFilter={typeFilter}
        onTypeFilterChange={handleTypeFilterChange}
        onTodayClick={goToToday}
        onNavigate={changePeriod}
        onViewChange={handleViewChange}
        onTrainerFilterClick={() => setOpenTrainerFilter(true)}
        onSettingsClick={() => setOpenScheduleSetting(true)}
        visibleCounts={scheduleSetting.visibleCounts}
        onAddSchedule={handlePanelAddSchedule}
        onDateChange={handleDateChange}
        customRange={customRange}
        onApplyRange={handleApplyRange}
        onClearRange={handleClearRange}
        zoomPercent={Math.round((zoomValues[zoomViewKey] / ZOOM_CONFIG[zoomViewKey].base) * 100)}
        canZoomOut={zoomValues[zoomViewKey] > ZOOM_CONFIG[zoomViewKey].min}
        canZoomIn={zoomValues[zoomViewKey] < ZOOM_CONFIG[zoomViewKey].max}
        onZoomIn={() => handleZoom('in')}
        onZoomOut={() => handleZoom('out')}
      />

      {/* 기간 이동 리페치 인디케이터 — 이전 데이터 유지 중 (초기 로드는 기존 패턴 유지) */}
      {isFetchingEvents && !isLoading && (
        <FetchingBadge>
          <CircularProgress size={16} thickness={4} sx={{ color: '#4AB3BC' }} />
          불러오는 중...
        </FetchingBadge>
      )}

      {!isLoading &&
        events &&
        (view === 'month' || (isRangeMode && view !== 'week') ? (
          <PrivateLessonMonthView
            currentDate={currentDate}
            weekStartDay={scheduleSetting.weekStartDay}
            rangeStart={rangeMoments?.start}
            rangeEnd={rangeMoments?.end}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            events={visibleEvents}
            staffList={staffList}
            selectedTrainerIds={selectedTrainerIds}
            cellHeight={monthCellHeight}
            panelWidth={panelWidth}
            panelCollapsed={panelCollapsed}
            onTogglePanel={togglePanelCollapsed}
            onPanelWidthChange={handlePanelWidthChange}
            onEventClick={handleEventClick}
            onEventContextMenu={schedulePaste.openCardMenu}
            onEventDrop={handleMonthEventDrop}
            canDragEvent={canPrivateEdit}
            onDayCellContextMenu={handleMonthCellContextMenu}
            onGoToDay={goToDayView}
            onAddSchedule={handlePanelAddSchedule}
            onGroupJump={handleGroupJump}
            ticketAlertCount={ticketAlertCount}
            ticketAlertCountUnit={ticketAlertCountUnit}
            typeFilter={typeFilter}
          />
        ) : view === 'week' ? (
          periodTimes && (
            <PrivateLessonWeekView
              weekDates={isRangeMode && rangeDates ? rangeDates : weekDates}
              events={visibleEvents}
              staffList={staffList}
              selectedTrainerIds={selectedTrainerIds}
              periodTimes={periodTimes}
              selectedDate={selectedDate}
              onSelectDate={handleSelectDate}
              hourHeight={weekHourHeight}
              timeUnitMinutes={timeUnitMinutes}
              snapMinutes={snapMinutes}
              cardMinMinutes={scheduleSetting.cardMinMinutes}
              panelWidth={panelWidth}
              panelCollapsed={panelCollapsed}
              onTogglePanel={togglePanelCollapsed}
              onPanelWidthChange={handlePanelWidthChange}
              onEventClick={handleEventClick}
              onGoToDay={goToDayView}
              onAddSchedule={handlePanelAddSchedule}
              onEmptyCellClick={handleWeekCellCreate}
              onEmptyCellContextMenu={handleWeekCellContextMenu}
              onEventContextMenu={schedulePaste.openCardMenu}
              onEventDrop={handleWeekEventDrop}
              canDragEvent={canPrivateEdit}
              onGroupJump={handleGroupJump}
              ticketAlertCount={ticketAlertCount}
              ticketAlertCountUnit={ticketAlertCountUnit}
              typeFilter={typeFilter}
            />
          )
        ) : (
          hours.length > 0 && (
            <PrivateLessonCalendar
              hours={hours}
              trainers={staffList}
              filteredTrainers={filteredTrainers}
              periodTimes={periodTimes}
              events={visibleEvents}
              currentDate={currentDate}
              cellHeight={cellHeight}
              timeUnitMinutes={timeUnitMinutes}
              snapMinutes={snapMinutes}
              cardMinMinutes={scheduleSetting.cardMinMinutes}
              visibleCounts={scheduleSetting.visibleCounts}
              onCellClick={handleAddEvent}
              onCellContextMenu={handleDayCellContextMenu}
              onEventClick={handleEventClick}
              onEventContextMenu={schedulePaste.openCardMenu}
              onDragTimeChange={handleDragTimeChange}
              canDragEvent={canPrivateEdit}
              onTrainerMove={handleTrainerMove}
              onTrainerReorder={handleTrainerReorder}
              typeFilter={typeFilter}
              ticketAlertCount={ticketAlertCount}
              ticketAlertCountUnit={ticketAlertCountUnit}
            />
          )
        ))}

      <SchedulePasteLayer paste={schedulePaste} remove={scheduleDelete} />

      {moveConfirm.element}

      <ScheduleDropModal
        drop={dropAsk}
        onMove={confirmDropMove}
        onCopy={confirmDropCopy}
        onClose={() => setDropAsk(null)}
      />

      <MoveTrainerAssignModal
        open={!!moveAssignAsk}
        fromTrainerName={moveAssignAsk?.event?.trainerName}
        trainerName={moveAssignAsk?.move?.newTrainer?.trainerName}
        targets={moveAssignAsk?.targets}
        onSelect={confirmMoveAssign}
        onClose={() => setMoveAssignAsk(null)}
      />

      <PrivateLessonEditModal
        open={open}
        isUpdate={isUpdate}
        modalData={modalData}
        setModalData={setModalData}
        rooms={rooms}
        tickets={tickets}
        searchResults={searchResults}
        setSearchResults={setSearchResults}
        selectedMembers={selectedMembers}
        setSelectedMembers={setSelectedMembers}
        isMemberChanged={isMemberChanged}
        setIsMemberChanged={setIsMemberChanged}
        setTickets={setTickets}
        courseClassType={courseClassType}
        debouncedGetMembers={debouncedGetMembers}
        handleChangeMember={handleChangeMember}
        otMemberTickets={otMemberTickets}
        otTicketErrors={otTicketErrors}
        onRetryOtTickets={retryOtTickets}
        handleCancel={handleCancel}
        onConflict={rememberConflict}
        handleSaveEvent={handleSaveEvent}
        onRefresh={refetchEvents}
        trainerOptions={filteredTrainers}
        trainerSelectable={trainerSelectMode}
        getCreateDefaults={createDefaultsFor}
      />

      <BranchScheduleSettingModal
        open={openScheduleSetting}
        onClose={closeScheduleSetting}
        onPreviewChange={setSchedulePreview}
        branchId={user?.branch?.branchId}
        token={user?.token}
        periodTimes={savedPeriodTimes}
        onPeriodTimesSaved={onPeriodTimesSaved}
        setting={savedScheduleSetting}
        staffList={savedStaffList}
        settingLoading={scheduleSettingLoading}
        settingError={scheduleSettingError}
        rooms={rooms}
        canManageRooms={canManageRooms}
      />

      {/* 여러 명 OT 는 그룹레슨과 같은 메뉴(참석관리·수업수정·수업취소) */}
      {isOtGroupEvent(modalData) && (
        <GroupEventMenu
          anchorEl={anchorEl}
          open={opens.open}
          onClose={() => setOpens(prev => ({ ...prev, open: false }))}
          eventData={{
            className: 'OT',
            classDate: modalData.classDate,
            startTime: modalData.startTime,
            endTime: modalData.endTime,
            trainerName: modalData.trainerName,
            branchExerciseRoomName: modalData.branchExerciseRoomName,
            maxMemberCount: modalData.membersMaxCount,
            reservedMemberCount: modalData.reservedMemberCount || 0,
            attendedMemberCount: modalData.attendedMemberCount || 0,
            absentedMemberCount: modalData.absentedMemberCount || 0,
          }}
          hideWaiting
          hideCancel
          canAttendance={canPrivateAttendance}
          canEdit={canPrivateEdit}
          onAttendance={() => setOpens(prev => ({ ...prev, open: false, open1: true }))}
          onEdit={() => {
            setOpens(prev => ({ ...prev, open: false }));
            setSearchResults([]);
            setOpen(true);
          }}
        />
      )}
      <EventDetailMenu
        open={opens.open && !isOtGroupEvent(modalData)}
        anchorEl={anchorEl}
        modalData={modalData}
        canEdit={canPrivateEdit}
        canAttendance={canPrivateAttendance}
        onMemberNameClick={
          modalData?.courseClassType === 'CONSULTATION' &&
          modalData?.consultType !== 'NON_MEMBER' &&
          modalData?.consultationId
            ? handleConsultMemberClick
            : (modalData?.courseClassType === 'PRIVATE_LESSON' ||
                  modalData?.courseClassType === 'DUET_LESSON' ||
                  modalData?.courseClassType === 'OT') &&
                modalData?.memberId
              ? handleLessonMemberClick
              : undefined
        }
        onClose={() => setOpens(prev => ({ ...prev, open: false }))}
        onAttendanceClick={() => {
          setOpens(prev => ({ ...prev, open: false }));
          // 상담인 경우 상담 상세 모달 열기
          if (modalData?.courseClassType === 'CONSULTATION') {
            setOpenConsultationDetail(true);
          } else {
            setOpens(prev => ({ ...prev, open1: true }));
          }
        }}
        onEditClick={async () => {
          setOpens(prev => ({ ...prev, open: false }));

          // 상담인 경우 전체 상담 데이터를 API에서 가져옴
          if (modalData?.courseClassType === 'CONSULTATION' && modalData?.consultationId) {
            try {
              const consultation = await getConsultationById(modalData.consultationId, user.token);
              applyConsultationToModal(consultation);
              // consultation을 찾지 못한 경우 modalData의 기존 데이터 사용
            } catch (error) {
              console.error('상담 정보 조회 실패:', error);
              // API 실패 시에도 modalData의 기존 데이터로 모달 열기
            }
          }

          // 기존 회원 이름으로 검색하여 tickets 포함된 최신 데이터 로드
          setSearchResults([]);
          setOpen(true);
        }}
      />

      <CancelConfirmModal
        open={opens.open3}
        onClose={() => setOpens(prev => ({ ...prev, open3: false, open2: false }))}
        onConfirm={deleteWeek}
      />

      {/* 여러 명 OT·듀엣은 그룹레슨 참석관리 모달(회원 추가·개별 출결·개별 취소), 그 외는 개인 참석관리 모달 */}
      {isOtGroupEvent(modalData) || modalData?.courseClassType === 'DUET_LESSON' ? (
        opens.open1 && (
          <LessonUsers
            custom
            opens={opens}
            setOpens={setOpens}
            lesson={otLesson}
            anchorEl={modalData}
            onEventDeleted={handleEventDeleted}
          />
        )
      ) : (
        <PrivateLessonAttendanceModal
          open={opens.open1}
          onClose={() => setOpens(prev => ({ ...prev, open1: false }))}
          lesson={modalData}
          onEventDeleted={handleEventDeleted}
        />
      )}

      <TrainerFilterModal
        open={openTrainerFilter}
        onClose={() => setOpenTrainerFilter(false)}
        trainers={staffList}
        selectedTrainerIds={selectedTrainerIds}
        onTrainerCheckbox={handleTrainerCheckbox}
        onSelectAllTrainers={handleSelectAllTrainers}
        onReset={() => setSelectedTrainerIds([])}
        onApply={() => setOpenTrainerFilter(false)}
      />

      <ConsultationDetailModal
        open={openConsultationDetail}
        onClose={() => setOpenConsultationDetail(false)}
        consultationData={modalData}
        onEdit={consultation => {
          applyConsultationToModal(consultation);
          setSearchResults([]);
          setOpenConsultationDetail(false);
          setOpen(true);
        }}
      />
    </>
  );
};

export default PrivateLessonList;
